import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { api, Topic, Template } from '../api/rest';
import { currentIdToken } from '../auth/cognito';
import { relativeTime } from '../lib/time';
import { useT } from '../i18n';

export function AdminTemplates() {
  const t = useT();
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [editing, setEditing] = useState<Template | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    void currentIdToken().then((tok) => {
      if (!tok) {
        navigate('/admin', { replace: true });
        return;
      }
      void refresh();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const [ts, tpls] = await Promise.all([api.listTopics(), api.listTemplates()]);
      setTopics(ts);
      setTemplates(tpls);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  };

  // Resolve topic IDs to topics; a deleted topic simply won't be present.
  const topicMap = useMemo(() => {
    const m = new Map<string, Topic>();
    topics.forEach((tp) => m.set(tp.id, tp));
    return m;
  }, [topics]);

  const startGame = async (tpl: Template) => {
    const ids = tpl.topicOrder.filter((id) => topicMap.has(id)); // skip deleted topics
    if (ids.length === 0) {
      setError('templates.noTopicsLeft');
      return;
    }
    setBusyId(tpl.id);
    setError(null);
    try {
      const game = await api.createGame(ids);
      navigate(`/admin/console/${game.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create game');
      setBusyId(null);
    }
  };

  const removeTemplate = async (tpl: Template) => {
    if (!confirm(t('templates.deleteConfirm', { name: tpl.name }))) return;
    try {
      await api.deleteTemplate(tpl.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  return (
    <div className="stack">
      <div className="rowbetween">
        <h1>{t('templates.title')}</h1>
        <Link to="/admin/games" className="ghost">{t('common.back')}</Link>
      </div>

      {error && <div className="banner error">{t(error)}</div>}

      {editing ? (
        <TemplateEditor
          topics={topics}
          topicMap={topicMap}
          template={editing === 'new' ? null : editing}
          onError={setError}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <>
          <div className="rowbetween">
            <p className="muted">{t('templates.intro')}</p>
            <button
              className="primary"
              onClick={() => {
                setError(null);
                setEditing('new');
              }}
            >
              {t('templates.new')}
            </button>
          </div>

          <div className="card">
            <h2>{t('templates.yourTemplates')}</h2>
            {templates.length === 0 ? (
              <p className="muted empty">{t('templates.none')}</p>
            ) : (
              <ul className="templatelist">
                {templates.map((tpl) => {
                  const count = tpl.topicOrder.filter((id) => topicMap.has(id)).length;
                  return (
                    <li key={tpl.id}>
                      <span className="ttitle">{tpl.name}</span>
                      <span className="chip subtle">{t('templates.topicCount', { n: count })}</span>
                      <span className="muted small when">{relativeTime(tpl.updatedAt ?? tpl.createdAt) ?? ''}</span>
                      <button
                        className="ghost small"
                        onClick={() => {
                          setError(null);
                          setEditing(tpl);
                        }}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        className="primary small"
                        disabled={count === 0 || busyId === tpl.id}
                        onClick={() => startGame(tpl)}
                      >
                        {busyId === tpl.id ? t('games.creating') : t('templates.startGame')}
                      </button>
                      <button className="danger small" onClick={() => removeTemplate(tpl)}>{t('common.delete')}</button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function TemplateEditor({
  topics,
  topicMap,
  template,
  onError,
  onDone,
  onCancel,
}: {
  topics: Topic[];
  topicMap: Map<string, Topic>;
  template: Template | null;
  onError: (m: string) => void;
  onDone: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(template?.name ?? '');
  // Drop any topics that no longer exist so the editor only shows live topics.
  const [order, setOrder] = useState<string[]>(
    (template?.topicOrder ?? []).filter((id) => topicMap.has(id)),
  );
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const sensors = useSensors(
    // A small delay/tolerance keeps taps and touch-scrolls from being hijacked as drags.
    useSensor(PointerSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      setOrder((cur) => arrayMove(cur, cur.indexOf(String(active.id)), cur.indexOf(String(over.id))));
    }
  };

  const addTopic = (id: string) => setOrder((cur) => (cur.includes(id) ? cur : [...cur, id]));
  const removeTopic = (id: string) => setOrder((cur) => cur.filter((x) => x !== id));

  const q = search.trim().toLowerCase();
  const available = topics
    .filter((tp) => !order.includes(tp.id) && tp.title.toLowerCase().includes(q))
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));

  const save = async () => {
    if (!name.trim()) {
      onError('templates.nameRequired');
      return;
    }
    setBusy(true);
    try {
      if (template) await api.updateTemplate(template.id, name.trim(), order);
      else await api.createTemplate(name.trim(), order);
      await onDone();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to save template');
      setBusy(false);
    }
  };

  return (
    <div className="card stack">
      <div className="section-head">
        <h2>{template ? t('templates.editTitle') : t('templates.newTitle')}</h2>
      </div>

      <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('templates.namePlaceholder')} />

      <h3 className="muted">{t('templates.lineup')}</h3>
      {order.length === 0 ? (
        <p className="muted empty">{t('templates.emptyLineup')}</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            <ul className="sortlist">
              {order.map((id, idx) => (
                <SortableRow
                  key={id}
                  id={id}
                  index={idx}
                  title={topicMap.get(id)?.title ?? id}
                  onRemove={() => removeTopic(id)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <h3 className="muted">{t('templates.addTopics')}</h3>
      {topics.length > 6 && (
        <div className="toolbar">
          <div className="searchbox">
            <span className="searchicon">🔍</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('games.filterTopics')} />
          </div>
        </div>
      )}
      <ul className="selectlist">
        {available.map((tp) => (
          <li key={tp.id} onClick={() => addTopic(tp.id)}>
            <span className="ttitle">{tp.title}</span>
            <span className="order">＋</span>
          </li>
        ))}
        {available.length === 0 && <li className="muted">{t('templates.allAdded')}</li>}
      </ul>

      <div className="row">
        <button className="primary" disabled={busy || !name.trim()} onClick={save}>
          {busy ? t('games.creating') : t('common.save')}
        </button>
        <button className="ghost" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function SortableRow({
  id,
  index,
  title,
  onRemove,
}: {
  id: string;
  index: number;
  title: string;
  onRemove: () => void;
}) {
  const t = useT();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <li ref={setNodeRef} style={style} className="sortrow">
      <button className="draghandle" {...attributes} {...listeners} aria-label={t('templates.dragHandle')}>⠿</button>
      <span className="order">#{index + 1}</span>
      <span className="ttitle">{title}</span>
      <button className="danger small" onClick={onRemove}>{t('common.delete')}</button>
    </li>
  );
}
